import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import apiUtils from '../../global/utils/api';

import {
  handleCreateFulfilled
  , handleFetchSinglePending
  , handleFetchSingleFulfilled
  , handleFetchSingleFromListFulfilled
  , handleFetchSingleRejected
  , handleFetchListPending
  , handleFetchListFulfilled
  , handleFetchListRejected
  , handleMutationPending
  , handleMutationFulfilled
  , handleMutationRejected
  , handleDeletePending
  , handleDeleteFulfilled
  , handleDeleteRejected
  , shouldFetch
  , INITIAL_STATE
  , handleInvalidateQuery
  , handleInvalidateQueries
  , handleAddSingleToList
} from '../../global/utils/storeUtils';


// First define all API calls for contentModule
// define and export the strings for the different specific product endpoints once here.
// we'll catch for these strings on the server side and apply the correct permissions to the query.
// these are passed in to the productService hooks at the component level as the endpoint argument.
// export const myExampleEndpoint = 'example-endpoint';

/**
 * The functions below, called thunks, allow us to perform async logic. They
 * can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
 * will call the thunk with the `dispatch` function as the first argument. Async
 * code can then be executed and other actions can be dispatched. Thunks are
 * typically used to make async requests.
 * 
 * In practice we won't dispatch these directly, they will be dispatched by contentModuleService which has a nicer api built on hooks.
 */

// this `redstone` prefix means the endpoint is on the redstone server, not rome.
// this endpoint does work but is not currently used, here as an example
export const contentModulesByUserEndpoint = (userId) => {
  if(!userId) return null;
  return `redstone/by-user/${userId}`;
}
// TODO: add grant's new redstone endpoint here that returns a user's training modules and cert status stuff
export const contentModulesByGroupUserWithCompletionEndpoint = (groupUserId) => {
  if(!groupUserId) return null;
  return `redstone/by-group-user-with-completion-data/${groupUserId}`;
}

// by group because from there we can get org and all cms tied to either
export const contentModulesByGroupAvailabilityEndpoint = (groupId) => {
  if(!groupId) return null;
  return `by-group-availability/${groupId}`; 
}

// CREATE
export const sendCreateContentModule = createAsyncThunk(
  'contentModule/sendCreate'
  , async (newContentModule) => {
    const endpoint = `/api/content-modules`;
    const response = await apiUtils.callAPI(endpoint, 'POST', newContentModule);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// READ
export const fetchSingleContentModule = createAsyncThunk(
  'contentModule/fetchSingle'
  , async (id) => {
    const endpoint = `/api/content-modules/${id}`;
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);
export const fetchContentModuleList = createAsyncThunk(
  'contentModule/fetchList' // this is the action name that will show up in the console logger.
  , async (listArgs) => {
    const endpoint = `/api/content-modules${listArgs}`;
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// for each resource we can add as many endpoints as we want in this format and we only need two actions to handle them.
// this will hit the same endpoint as the list version, but the store will handle the returned array and access the single item in it.
export const fetchSingleContentModuleAtEndpoint = createAsyncThunk(
  'contentModule/fetchSingleWithFilter' // this is the action name that will show up in the console logger.
  , async (query) => {
    const endpoint = `/api/content-modules${query}`
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);
export const fetchContentModuleListAtEndpoint = createAsyncThunk(
  'contentModule/fetchListWithFilter' // this is the action name that will show up in the console logger.
  , async (query) => {
    // catch for the redstone prefix and call the correct api function
    const callAPI = query.includes('/redstone') ? apiUtils.callRedstoneAPI : apiUtils.callAPI;
    const endpoint = `/api/content-modules${query}`; // example: `/api/content-modules/logged-in?${queryString}`
    const response = await callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// UPDATE
export const sendUpdateContentModule = createAsyncThunk(
  'contentModule/sendUpdate'
  , async ({ _id, ...updates }) => {
    const endpoint = `/api/content-modules/${_id}`;
    const response = await apiUtils.callAPI(endpoint, 'PUT', updates);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// DELETE
export const sendDeleteContentModule = createAsyncThunk(
  'contentModule/sendDelete'
  , async (id) => {
    const endpoint = `/api/content-modules/${id}`;
    const response = await apiUtils.callAPI(endpoint, 'DELETE');
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// next define the store's initial state, all of our store utils rely on a specific state shape, so use the constant
const initialState = { ...INITIAL_STATE };

// define the contentModuleSlice. This is a combination of actions and reducers. More info: https://redux-toolkit.js.org/api/createSlice
export const contentModuleSlice = createSlice({
  name: 'contentModule'
  , initialState
  /**
   * The `reducers` field lets us define reducers and generate associated actions.
   * Unlike the selectors defined at the bottom of this file, reducers only have access
   * to this specific reducer and not the entire store.
   * 
   * Again, we will not dispatch these directly, they will be dispatched by contentModuleService.
   */
  , reducers: {
    invalidateQuery: handleInvalidateQuery
    , invalidateQueries: handleInvalidateQueries
    , addContentModuleToList: handleAddSingleToList
  }

  /**
   * The `extraReducers` field lets the slice handle actions defined elsewhere,
   * including actions generated by createAsyncThunk or in other slices.
   * We'll use them to track our server request status.
   * 
   * We'll add a case for each API call defined at the top of the file to dictate
   * what happens during each API call lifecycle.
   */
  , extraReducers: (builder) => {
    builder
      // CREATE
      .addCase(sendCreateContentModule.fulfilled, handleCreateFulfilled)

      // READ
      .addCase(fetchSingleContentModule.pending, handleFetchSinglePending)
      .addCase(fetchSingleContentModule.fulfilled, handleFetchSingleFulfilled)
      .addCase(fetchSingleContentModule.rejected, handleFetchSingleRejected)
      .addCase(fetchContentModuleList.pending, handleFetchListPending)
      // because lists are returned from the server named for their resource, we need to pass a `listKey` so the util can properly handle the response
      .addCase(fetchContentModuleList.fulfilled, (state, action) => handleFetchListFulfilled(state, action, 'contentModules'))
      .addCase(fetchContentModuleList.rejected, handleFetchListRejected)

      // permission protected single fetches
      .addCase(fetchSingleContentModuleAtEndpoint.pending, handleFetchSinglePending)
      // these endpoints return named lists, we need to pass a `listKey` so the util can properly handle the response
      .addCase(fetchSingleContentModuleAtEndpoint.fulfilled, (state, action) => handleFetchSingleFromListFulfilled(state, action, 'contentModules'))
      .addCase(fetchSingleContentModuleAtEndpoint.rejected, handleFetchSingleRejected)
      // permission protected list fetches
      .addCase(fetchContentModuleListAtEndpoint.pending, handleFetchListPending)
      .addCase(fetchContentModuleListAtEndpoint.fulfilled, (state, action) => handleFetchListFulfilled(state, action, 'contentModules'))
      .addCase(fetchContentModuleListAtEndpoint.rejected, handleFetchListRejected)

      // UPDATE
      .addCase(sendUpdateContentModule.pending, handleMutationPending)
      .addCase(sendUpdateContentModule.fulfilled, handleMutationFulfilled)
      .addCase(sendUpdateContentModule.rejected, handleMutationRejected)
      // .addCase(sendUpdateContentModule.fulfilled, (state, action) => handleMutationFulfilled(state, action, (newState, action) => {
      //   // by passing this optional callback we now have access to the new state if we want to do something else with it, this works for all reducer handlers
      // }))

      // DELETE
      .addCase(sendDeleteContentModule.pending, handleDeletePending)
      .addCase(sendDeleteContentModule.fulfilled, handleDeleteFulfilled)
      .addCase(sendDeleteContentModule.rejected, handleDeleteRejected)
  }
});

// export the actions for the reducers defined above
export const { invalidateQuery, invalidateQueries, addContentModuleToList } = contentModuleSlice.actions;


// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
export const fetchListIfNeeded = (queryKey, listFetch = fetchContentModuleList) => (dispatch, getState) => {
  const contentModuleQuery = getState().contentModule.listQueries[queryKey];
  if(shouldFetch(contentModuleQuery)) {
    // console.log('Fetching contentModule list', queryKey);
    dispatch(listFetch(queryKey));
  } else {
    // console.log('No need to fetch, fresh query in cache');
  }
};

export const fetchSingleIfNeeded = (id, singleFetch = fetchSingleContentModule) => (dispatch, getState) => {
  const contentModuleQuery = getState().contentModule.singleQueries[id];
  if(shouldFetch(contentModuleQuery)) {
    dispatch(singleFetch(id));
  } else {
    // console.log('No need to fetch, fresh query in cache');
  }
}

export default contentModuleSlice.reducer;
